import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback } from 'react';

/**
 * Navigation hook that automatically preserves the ?audit and ?onboarding query params when navigating.
 *
 * Usage:
 * ```tsx
 * const navigate = usePreserveParamsNavigation();
 * navigate('/app/plans/123/releases/456');
 * // With ?audit=789 in current URL becomes: /app/plans/123/releases/456?audit=789
 * // With ?onboarding in current URL becomes: /app/plans/123/releases/456?onboarding
 * ```
 */
export const usePreserveParamsNavigation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auditId = searchParams.get('audit');
  const hasOnboarding = searchParams.has('onboarding');

  const preserveParamsNavigate = useCallback(
    (to: string, options?: { replace?: boolean; state?: any }) => {
      let modifiedTo = to;

      // If we're in audit mode and the target doesn't already have ?audit, append it
      if (auditId && !to.includes('?audit=') && !to.includes('&audit=')) {
        const separator = to.includes('?') ? '&' : '?';
        modifiedTo = `${modifiedTo}${separator}audit=${auditId}`;
      }

      // If we're in onboarding mode and the target doesn't already have ?onboarding, append it
      if (hasOnboarding && !to.includes('?onboarding') && !to.includes('&onboarding')) {
        const separator = modifiedTo.includes('?') ? '&' : '?';
        modifiedTo = `${modifiedTo}${separator}onboarding`;
      }

      navigate(modifiedTo, options);
    },
    [navigate, auditId, hasOnboarding]
  );

  return preserveParamsNavigate;
};

/**
 * Helper to check if we're currently in audit mode
 */
export const useIsAuditMode = (): boolean => {
  const [searchParams] = useSearchParams();
  return searchParams.has('audit');
};

/**
 * Helper to check if we're currently in onboarding mode
 */
export const useIsOnboardingMode = (): boolean => {
  const [searchParams] = useSearchParams();
  return searchParams.has('onboarding');
};

/**
 * Get the current audit ID from query params
 */
export const useAuditId = (): string | null => {
  const [searchParams] = useSearchParams();
  return searchParams.get('audit');
};
